import React, { useMemo } from 'react';
import { User } from '../../types';
import {
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import DataTable, { Column, EmptyState, PaginationConfig } from '../common/DataTable';
import Badge from '../common/Badge';

interface UserListProps {
  users: User[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onEdit: (user: User) => void;
  onToggleActive: (id: number, isActive: boolean) => void;
  onDelete: (id: number) => void;
}

const UserList: React.FC<UserListProps> = ({
  users,
  loading,
  currentPage,
  totalPages,
  totalUsers,
  itemsPerPage,
  onPageChange,
  onEdit,
  onToggleActive,
  onDelete,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const columns: Column<User>[] = useMemo(() => [
    {
      key: 'user',
      header: 'User',
      render: (user) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {user.username}
          </div>
          <div className="text-sm text-gray-500">
            {user.email}
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user) => (
        <Badge
          label={user.role}
          variant={user.role === 'admin' ? 'purple' : 'success'}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user) => (
        <Badge
          label={user.is_active ? 'Active' : 'Inactive'}
          variant={user.is_active ? 'success' : 'error'}
        />
      ),
    },
    {
      key: 'created',
      header: 'Created',
      render: (user) => (
        <span className="text-sm text-gray-500">
          {formatDate(user.created_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (user) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => onEdit(user)}
            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
            title="Edit user"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => onToggleActive(user.id, user.is_active)}
            className={`p-1 rounded ${
              user.is_active
                ? 'text-red-600 hover:text-red-900 hover:bg-red-50'
                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
            }`}
            title={user.is_active ? 'Deactivate user' : 'Activate user'}
          >
            {user.is_active ? (
              <XCircleIcon className="h-4 w-4" />
            ) : (
              <CheckCircleIcon className="h-4 w-4" />
            )}
          </button>
          
          <button
            onClick={() => onDelete(user.id)}
            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
            title="Delete user"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ], [onEdit, onToggleActive, onDelete]);

  const emptyState: EmptyState = {
    icon: UsersIcon,
    title: 'No users found',
    description: 'Get started by creating a new user account.',
  };

  const pagination: PaginationConfig | undefined = totalPages > 1 ? {
    currentPage,
    totalPages,
    totalItems: totalUsers,
    itemsPerPage,
    onPageChange,
  } : undefined;

  return (
    <DataTable
      data={users}
      columns={columns}
      keyExtractor={(user) => user.id}
      title="Users"
      titleCount={totalUsers}
      emptyState={emptyState}
      pagination={pagination}
      loading={loading}
      showLoadingInHeader={true}
    />
  );
};

export default UserList;